/**
 * 76. Expense Reports — submission; receipts; approval;
 * reimbursement.
 */
import {
  Card,
  PageHeader,
  Button,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatDate, formatMoney } from "@/lib/money";

interface ExpenseRow {
  id: string;
  claimNumber: string;
  employee: string;
  submissionDate: string | null;
  total: string;
  currency: string;
  lineCount: number;
  status: "Draft" | "Submitted" | "Approved" | "Reimbursed" | "Rejected";
}

export function ExpenseReportsPage() {
  const { data, isLoading } = useApiList<ExpenseRow>(
    ["expenses", "claims"],
    "/expenses/claims",
  );

  return (
    <div>
      <PageHeader
        title="Expense Reports"
        subtitle="Submission, receipts, approval, and reimbursement."
        actions={<Button variant="primary">New expense report</Button>}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Expense claims (${data.total})`}>
          <DataTable<ExpenseRow>
            rows={data.items}
            columns={[
              {
                key: "claimNumber",
                label: "Claim",
                render: (r) => (
                  <span className="atlas-mono">{r.claimNumber}</span>
                ),
              },
              { key: "employee", label: "Employee" },
              {
                key: "submissionDate",
                label: "Submitted",
                render: (r) =>
                  r.submissionDate ? formatDate(r.submissionDate) : "—",
              },
              { key: "lineCount", label: "Lines", numeric: true },
              {
                key: "total",
                label: "Total",
                numeric: true,
                render: (r) => formatMoney(r.total, r.currency),
              },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Approved" || r.status === "Reimbursed" ? (
                    <StatusBadge status={r.status} />
                  ) : r.status === "Rejected" ? (
                    <Badge tone="danger">{r.status}</Badge>
                  ) : r.status === "Submitted" ? (
                    <Badge tone="warning">{r.status}</Badge>
                  ) : (
                    <Badge tone="default">{r.status}</Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
